// React y librerías
import { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";

// Componentes GTI
import { GTIWebButton, GTIWebAlert } from "@gti-controls-v2/web-core";

// Material UI
import {
  Paper, Grid, Stack, Box,
  CircularProgress
} from "@gti-controls-v2/web-core";
import {
  Error, DoNotDisturbAlt, CheckCircle, ArrowBack
} from "@gti-controls-v2/web-core";

// Estilos y constantes
import loginStyles from "../../common/styles/Login";
import customTheme from "../../theme";
import { paths } from "../../common/constants/Rutas";

// Hooks personalizados
import useInputValidator, { useInputValidatorEmails } from "../../hooks/useInputValidator";
import { useMediaQueryCustom } from "../../hooks/useMediaQueryCustom/useMediaQueryCustom";
import useRecovery from "../../hooks/useRecovery";

// Componentes internos
import LoginLayout from "../../layouts/Login/LoginLayout";
import SendRecoveryEmail from "./sendRecoveryOpt";
import FormRecovery from "./formRecovery";
import ChangePassword from "./changePass";

// Recursos
import logo from "../../assets/images/logo-masorden-gris-small.svg";

const ForgotPassword =  () => {
    const styles = loginStyles;
	const navigate = useNavigate();
	const [isLoading, setIsLoading] = useState(false);
	const [error, setError] = useState(null);
	const { validateInputEmail: inputValidator } = useInputValidatorEmails();
    const { validateInput: validateInputPasswords } = useInputValidator();
	const { isMobile480, isTablet768, isTablet1024 } = useMediaQueryCustom();
    const { sendAdminRecovery, confirmAdminRecovery, confirmAdminSignup } = useRecovery()
    const [formData, setFormData] = useState({
        usernameEmail: "",
        userState: null,
        userLegend: "Entra con el dato que tienes registrado",
        userIcon: null,

        password: "",
        passwordState: null,
        passwordLegend: "Debe contener mínimo 8 caracteres con: 1 mayúscula y 1 minúscula",

        passwordConfirm: "",
        confirmState: null,
        confirmLegend: ""
    });
    const [recoveryInfo, setRecoveryInfo] = useState({
        email: '',
        code: '',
        visible: true,
        tile: "Restablece tu contraseña",
        buttonText: "Recuperar",
        type: "1"
    });
    const [sendRecoveryView, setSendRecoveryView] = useState(false);
    const typeProc = {
        confirmacion: "1",
        recupera: "2"
    };
    const location = useLocation();

    useEffect(() => {
        if (location.search) {
            const params = new URLSearchParams(location.search);
            const code = params.get('code');
            const email = params.get('email');
            const type = params.get('type');

            setRecoveryInfo({
                email: email,
                code: code,
                visible: (email && code),
                tile: "Cambia tu contraseña",
                buttonText: "Cambiar contraseña",
                type: type
            });
        } else {
            setRecoveryInfo({
                email: '',
                code: '',
                visible: false,
                tile: "Restablece tu contraseña",
                buttonText: "Recuperar"
            });
        }
    }, [location]);

    const redirectLogin = () => {
        navigate(paths.Login);
    };
    
    const handleSubmit = async (e) => {
        if (recoveryInfo.visible) {
            await confirmRecovery(e);
        } else {
            await sendRecovery(e);
        }
    };

    const confirmRecovery = async (e) => {
        e.preventDefault();

        setIsLoading(true);
        if (validateForm()) {
            const response = typeProc.recupera === recoveryInfo.type ? await confirmAdminRecovery(recoveryInfo.email, formData.password, recoveryInfo.code) : await confirmAdminSignup(recoveryInfo.email, formData.password, recoveryInfo.code);
            
            if ( response?.exitoso === false ) {
                if (typeof response.data === 'string') {
                    setFormData({
                        ...formData,
                        userLegend: response.data,
                        userState: "error",
                        userIcon: <Error style={{ color: "#b71c1c" }} />
                    });
                } else {
                    setError({
                        title: "No se pudo restablecer tu contraseña",
                        message: response.data.errorDescription,
                    });
                }
            }

            if (response?.data === recoveryInfo.email) {
                initValues();
                redirectLogin();
            }            
        }

        setIsLoading(false);
    };

    const sendRecovery = async (e) => {
        e.preventDefault();
        
        setIsLoading(true);

        if (validateForm()) {
            const response = await sendAdminRecovery(formData.usernameEmail);

            if ( response?.exitoso === false ) {
                setError({
                    title: "No se pudo restablecer tu contraseña",
                    message: typeof response?.data === "string" ? response?.data : response?.data?.errorDescription ? response?.data?.errorDescription : "Tuvimos un problema al procesar tu solicitud. El servicio no está disponible en este momento. Te invitamos a intentarlo de nuevo más tarde." ,
                });

                setIsLoading(false);
                return;
            } else {
                setError(null);
            }

            initValues();
            setSendRecoveryView(true);
        }

        setIsLoading(false);
    };

    const validateForm = () => {
        let updatedFormData = { ...formData };

        if (recoveryInfo.visible) {
            const isPasswordEmpty = formData.password === "";
            const isConfirmEmpty = formData.passwordConfirm === "";

            updatedFormData = {
                ...updatedFormData,
                passwordLegend: isPasswordEmpty ? "Nueva contraseña es obligatoria" : formData.passwordLegend,
                passwordState: isPasswordEmpty ? "error" : formData.passwordState,
                confirmLegend: isConfirmEmpty ? "Confirmar contraseña es obligatoria" : formData.confirmLegend,
                confirmState: isConfirmEmpty ? "error" : formData.confirmState,
            };

            setFormData(updatedFormData);
            return !(updatedFormData.passwordState === "error" || updatedFormData.confirmState === "error");
        }

        const isEmailEmpty = formData.usernameEmail === "";

        updatedFormData = {
            ...updatedFormData,
            userLegend: isEmailEmpty ? "Correo electrónico es obligatorio" : formData.userLegend,
            userState: isEmailEmpty ? "error" : formData.userState,
            userIcon: isEmailEmpty ? <Error style={{ color: "#b71c1c" }} /> : formData.userIcon,
        };

        setFormData(updatedFormData);
        return updatedFormData.userState !== "error";
    };

    const initValues = () => {
        setFormData({
            usernameEmail: "",
            userState: null,
            userLegend: "Entra con el dato que tienes registrado",
            userIcon: null,
    
            password: "",
            passwordState: null,
            passwordConfirm: "",    
            passwordLegend: "Debe contener mínimo 8 caracteres con: 1 mayúscula y 1 minúscula"
        });
    };

    /** Constante que maneja los cambios en los campos */
    const handleChange = (e) => {
        setFormData({ ...formData, [e.target.name]: e.target.value.trim() });
    };

    const handleBlur = () => {
        inputValidator({ text: formData.usernameEmail }, (isValid) => {
            setFormData(prev => ({
                ...prev,
                userState: isValid ? "success" : "error",
                userLegend: isValid 
                    ? "Entra con el dato que tienes registrado"
                    : "Correo electrónico incompleto. Ejemplo: ejemplo@correo.com",
                userIcon: isValid 
                    ? <CheckCircle style={{ color: customTheme.paleta.success40 }} />
                    : <Error style={{ color: customTheme.paleta.error40 }} />
            }));
        });
    };

    const validatePasswords = () => {
        validateInputPasswords({ text: formData.password, isPassword: true }, (res) => {
            setFormData(prev => ({
                ...prev,
                passwordLegend: res.isValid ? "Debe contener mínimo 8 caracteres con: 1 mayúscula y 1 minúscula" : res.text,
                passwordState: res.isValid ? null : "error"
            }));
        });

        const passwordsMatch = formData.password === formData.passwordConfirm;

        setFormData(prev => ({
            ...prev,
            confirmLegend: passwordsMatch ? "" : "Las contraseñas no coinciden.",
            confirmState: passwordsMatch ? null : "error"
        }));
    };

    return (
        <LoginLayout>
            <Grid id="login-grid-form" item component={Paper} sx={styles.paperContainer}>
                <Stack
                    sx={styles.form} direction="column" spacing={isMobile480 ? 2 : 1}
                    padding={isMobile480 ? "16px 24px 16px 24px" : (isTablet768 || isTablet1024) ? "24px 24px 24px 24px" : "40px 24px 40px 24px"}
                    style={{ textAlign: "-webkit-left" }}
                    alignItems="center"
                >
                    <Box sx={styles.logoMoContainer}>
                        <Box
                            component="img"
                            src={logo}
                            alt="login"
                            sx={styles.logoMO}
                        />
                    </Box>

                    <Grid container alignItems="center">
                        <Grid item paddingRight={2}>
                            <GTIWebButton
                                icon={<ArrowBack />}
                                iconPosition="start"
                                onlyIcon={true}
                                onClick={()=> redirectLogin()}
                            />
                        </Grid>
                        <Grid item>
                            <Box sx={styles.title}>{recoveryInfo.tile}</Box>
                        </Grid>
                    </Grid>

                    {error && (
                        <GTIWebAlert
                            icon={<DoNotDisturbAlt />}
                            severity="error"
                            title={error.title}
                            text={error.message}
                            sx={{ maxWidth: "442px" }}
                        />
                    )}
                    
                    <Grid container padding={2}>
                        {
                            sendRecoveryView ? <SendRecoveryEmail /> : (
                                <form onSubmit={handleSubmit} style={{ width: '100%' }} >
                                    { 
                                        recoveryInfo.visible ? 
                                            <ChangePassword
                                                isLoading={isLoading}
                                                onBlurForm={validatePasswords} 
                                                onChangeForm={handleChange}
                                                value={formData}
                                            /> : 
                                            <FormRecovery 
                                                isLoading={isLoading}
                                                onBlurForm={handleBlur} 
                                                onChangeForm={handleChange}
                                                value={formData}
                                            />
                                    }
                                
                                    <Grid item sx={styles.buttonSpace}>
                                        <GTIWebButton
                                            onlyIcon={isLoading}
                                            icon={isLoading && <CircularProgress size={30} />}
                                            disabled={isLoading}
                                            iconPosition="end"
                                            type="submit"
                                            text={"Recuperar"}
                                        />
                                    </Grid>
                                </form>
                            )
                        }
                    </Grid>
                        
                </Stack>
            </Grid>
        </LoginLayout>
    )
};

export default ForgotPassword;